'use client';

import type { ComponentProps } from 'react';
import type { Theme, SxProps } from '@mui/material/styles';

import { useId } from 'react';
import { Icon } from '@iconify/react';
import { mergeClasses } from 'minimal-shared/utils';

import { styled } from '@mui/material/styles';

import { iconifyClasses } from './classes';
import { allIconNames, registerIcons } from './register-icons';

// ----------------------------------------------------------------------

const IconRoot = styled(Icon)``;

/**
 * ⚠️ `className`·`sx`·`width`·`height` 는 **선택으로 열어 둔다** — 하나라도 필수로 좁히면
 * 이 부품을 쓰는 수백 개 `.tsx` 가 통째로 컴파일 에러가 된다. 타입은 라이브러리
 * (`@iconify/react`)가 완비한 것을 물려 쓰고, 래퍼가 다시 좁히지 않는다.
 */
export type IconifyProps = Omit<
  ComponentProps<typeof IconRoot>,
  'icon' | 'sx' | 'width' | 'height'
> & {
  /** 아이콘 이름 (`'solar:filter-linear'`). `register-icons`에 없으면 온라인 로드 경고 */
  icon: string;
  /** 기본 20. `height`를 안 주면 높이도 이 값을 쓴다 */
  width?: number | string;
  height?: number | string;
  sx?: SxProps<Theme>;
};

export function Iconify({ className, icon, width = 20, height, sx, ...other }: IconifyProps) {
  const id = useId();

  if (!allIconNames.includes(icon)) {
    console.warn(
      [
        `Icon "${icon}" is currently loaded online, which may cause flickering effects.`,
        `To ensure a smoother experience, please register your icon collection for offline use.`,
        `More information is available at: https://docs.minimals.cc/icons/`,
      ].join('\n')
    );
  }

  registerIcons();

  return (
    <IconRoot
      ssr
      id={id}
      icon={icon}
      className={mergeClasses([iconifyClasses.root, className])}
      sx={[
        {
          width,
          flexShrink: 0,
          height: height ?? width,
          display: 'inline-flex',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    />
  );
}
